import { create } from "zustand";
import type { WorkflowDef } from "@/config/pipeline";
import { appEvents } from "@/shared/lib/events";
import { isActiveCompanionWorkflow } from "../lib/activeCompanionWorkflows";
import {
  CLOSED_COMPANION_DOCK_STATE,
  OPEN_COMPANION_DOCK_STATE,
  shouldAutoOpenCompanionDock,
  type CompanionDockAutoOpenReason,
} from "../lib/companionDockPolicy";

interface AgentChatState {
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  toggleChat: () => void;

  // Slash commands can trigger a route change after Marcus calls a tool. The
  // source page unmounts before the destination connects, so keep the dock
  // open across that one handoff instead of letting page-connect collapse it.
  slashCommandRouteHoldUntil: number | null;
  preserveOpenForSlashCommandNavigation: () => void;

  // Marcus onboarding drives users across pages. This lock keeps the dock
  // closed while the tour is narrating background route changes.
  marcusOnboardingDockLocked: boolean;
  setMarcusOnboardingDockLocked: (locked: boolean) => void;

  // Whether the docked side panel is hidden. Floating reopen FAB swaps in
  // until the user opens it or a policy-approved agent activity opens it.
  dockedPanelHidden: boolean;
  setDockedPanelHidden: (hidden: boolean) => void;
  requestDockAutoOpen: (reason: CompanionDockAutoOpenReason) => void;

  // True while a higher-level surface (e.g. the caption editor mounted inside
  // MagicEditOverlay) is hosting its own FloatingAgentCompanion. Page-level
  // panels respect this flag and unmount so Marcus's hooks/effects only run
  // once — the override instance passes `force` to bypass it.
  chatPanelOverlayMounted: boolean;
  setChatPanelOverlayMounted: (mounted: boolean) => void;

  activeChatId: string | null;
  setActiveChatId: (id: string | null) => void;

  workflowOverride: WorkflowDef | null;
  setWorkflowOverride: (wf: WorkflowDef | null) => void;

  // Increments on every mutation — triggers canvas re-render
  workflowVersion: number;
  incrementWorkflowVersion: () => void;

  // ── Agent Connection Lifecycle ──
  // Pages explicitly connect/disconnect the agent companion.
  // When disconnected, FloatingAgentCompanion renders nothing.
  connectedSessionId: string | null;
  connectedWorkflowSlug: string | null;
  connectedContext: Record<string, unknown> | null;
  // True when the session was just created (not resumed from server).
  // Used by FloatingAgentCompanion to skip the history fetch on fresh sessions.
  isNewSession: boolean;
  connectAgent: (
    sessionId: string,
    workflowSlug: string,
    isNewSession?: boolean,
    context?: Record<string, unknown> | null,
  ) => void;
  disconnectAgent: () => void;

  resetChatState: () => void;

  // Voice overlay state (shared between companion & canvas)
  isVoiceListening: boolean;
  liveVoiceTranscript: string;
  setVoiceListening: (listening: boolean) => void;
  setLiveVoiceTranscript: (text: string) => void;
}

const LOCKED_ONBOARDING_DOCK_STATE = CLOSED_COMPANION_DOCK_STATE;
const SLASH_COMMAND_ROUTE_HOLD_MS = 30_000;

function isSlashCommandRouteHoldActive(state: {
  slashCommandRouteHoldUntil: number | null;
}): boolean {
  return state.slashCommandRouteHoldUntil !== null && state.slashCommandRouteHoldUntil > Date.now();
}

export const useAgentChatStore = create<AgentChatState>((set) => ({
  chatOpen: false,
  setChatOpen: (open) =>
    set((s) =>
      s.marcusOnboardingDockLocked && open
        ? LOCKED_ONBOARDING_DOCK_STATE
        : { chatOpen: open, ...(open ? {} : { slashCommandRouteHoldUntil: null }) },
    ),
  toggleChat: () =>
    set((s) => {
      if (s.marcusOnboardingDockLocked) return LOCKED_ONBOARDING_DOCK_STATE;
      const chatOpen = !s.chatOpen;
      return { chatOpen, ...(chatOpen ? {} : { slashCommandRouteHoldUntil: null }) };
    }),

  slashCommandRouteHoldUntil: null,
  preserveOpenForSlashCommandNavigation: () =>
    set((s) =>
      s.marcusOnboardingDockLocked
        ? LOCKED_ONBOARDING_DOCK_STATE
        : {
            ...OPEN_COMPANION_DOCK_STATE,
            slashCommandRouteHoldUntil: Date.now() + SLASH_COMMAND_ROUTE_HOLD_MS,
          },
    ),

  marcusOnboardingDockLocked: false,
  setMarcusOnboardingDockLocked: (locked) =>
    set(
      locked
        ? {
            marcusOnboardingDockLocked: true,
            slashCommandRouteHoldUntil: null,
            ...LOCKED_ONBOARDING_DOCK_STATE,
          }
        : { marcusOnboardingDockLocked: false },
    ),

  dockedPanelHidden: true,
  setDockedPanelHidden: (hidden) =>
    set((s) =>
      s.marcusOnboardingDockLocked
        ? LOCKED_ONBOARDING_DOCK_STATE
        : {
            dockedPanelHidden: hidden,
            ...(hidden ? { slashCommandRouteHoldUntil: null } : {}),
          },
    ),
  requestDockAutoOpen: (reason) =>
    set((s) => {
      if (s.marcusOnboardingDockLocked) return LOCKED_ONBOARDING_DOCK_STATE;
      const holdActive = isSlashCommandRouteHoldActive(s);
      const slashCommandRouteHoldUntil = holdActive ? s.slashCommandRouteHoldUntil : null;
      if (reason === "page-connect" && holdActive) {
        return { ...OPEN_COMPANION_DOCK_STATE, slashCommandRouteHoldUntil: null };
      }
      if (!shouldAutoOpenCompanionDock(reason)) {
        return { ...CLOSED_COMPANION_DOCK_STATE, slashCommandRouteHoldUntil };
      }
      return { ...OPEN_COMPANION_DOCK_STATE, slashCommandRouteHoldUntil };
    }),

  chatPanelOverlayMounted: false,
  setChatPanelOverlayMounted: (mounted) => set({ chatPanelOverlayMounted: mounted }),

  activeChatId: null,
  setActiveChatId: (id) => set({ activeChatId: id }),

  workflowOverride: null,
  setWorkflowOverride: (wf) => set({ workflowOverride: wf }),

  workflowVersion: 0,
  incrementWorkflowVersion: () => set((s) => ({ workflowVersion: s.workflowVersion + 1 })),

  // ── Agent Connection Lifecycle ──
  connectedSessionId: null,
  connectedWorkflowSlug: null,
  connectedContext: null,
  isNewSession: false,

  connectAgent: (sessionId, workflowSlug, isNewSession = false, context = null) =>
    set((s) => {
      if (!isActiveCompanionWorkflow(workflowSlug)) {
        return {
          connectedSessionId: null,
          connectedWorkflowSlug: null,
          connectedContext: null,
          isNewSession: false,
          chatOpen: false,
          workflowOverride: null,
          dockedPanelHidden: true,
          slashCommandRouteHoldUntil: null,
        };
      }
      const slashCommandRouteHoldUntil = isSlashCommandRouteHoldActive(s)
        ? s.slashCommandRouteHoldUntil
        : null;
      return {
        connectedSessionId: sessionId,
        connectedWorkflowSlug: workflowSlug,
        connectedContext: context,
        isNewSession,
        slashCommandRouteHoldUntil,
        ...(s.marcusOnboardingDockLocked ? LOCKED_ONBOARDING_DOCK_STATE : {}),
      };
    }),

  disconnectAgent: () =>
    set((s) => {
      const holdActive = isSlashCommandRouteHoldActive(s);
      return {
        connectedSessionId: null,
        connectedWorkflowSlug: null,
        connectedContext: null,
        isNewSession: false,
        workflowOverride: null,
        ...(holdActive
          ? { slashCommandRouteHoldUntil: s.slashCommandRouteHoldUntil }
          : {
              chatOpen: false,
              dockedPanelHidden: true,
              slashCommandRouteHoldUntil: null,
            }),
      };
    }),

  resetChatState: () =>
    set({
      chatOpen: false,
      slashCommandRouteHoldUntil: null,
      marcusOnboardingDockLocked: false,
      activeChatId: null,
      workflowOverride: null,
      workflowVersion: 0,
      connectedSessionId: null,
      connectedWorkflowSlug: null,
      connectedContext: null,
      isNewSession: false,
      isVoiceListening: false,
      liveVoiceTranscript: "",
      dockedPanelHidden: true,
    }),

  // Voice overlay
  isVoiceListening: false,
  liveVoiceTranscript: "",
  setVoiceListening: (listening) => set({ isVoiceListening: listening }),
  setLiveVoiceTranscript: (text) => set({ liveVoiceTranscript: text }),
}));

appEvents.subscribe("companion:dock-auto-open", (event) => {
  useAgentChatStore.getState().requestDockAutoOpen(event.reason);
});
